// src/pages/SubmitStory.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { collection, addDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth, db, storage } from '../services/firebase';
import { useDropzone } from 'react-dropzone';
import { 
  ArrowLeft, 
  ArrowRight, 
  Check, 
  Upload, 
  X, 
  User, 
  Mail, 
  Phone, 
  Instagram, 
  Facebook, 
  Linkedin, 
  Globe,
  Image as ImageIcon,
  FileText,
  Shield,
  Loader2,
  AlertCircle
} from 'lucide-react';

const STEPS = [
  { id: 1, title: 'Basic Information', icon: User },
  { id: 2, title: 'Social Media', icon: Globe },
  { id: 3, title: 'Your Story', icon: FileText },
  { id: 4, title: 'Photos', icon: ImageIcon },
  { id: 5, title: 'Consent', icon: Shield }
];

export default function SubmitStory() {
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [autoSaveStatus, setAutoSaveStatus] = useState('');

  const [formData, setFormData] = useState({
    // Step 1: Basic Information
    fullName: user?.displayName || '',
    email: user?.email || '',
    phone: '',
    contactMethod: 'email',
    
    // Step 2: Social Media
    instagram: '',
    facebook: '',
    linkedin: '',
    website: '',
    
    // Step 3: Story
    story: '',
    storyTheme: '',
    
    // Step 4: Photos
    headshot: null,
    additionalPhotos: [],
    captions: {},
    
    // Step 5: Consent
    consent: false
  });

  const [headshotPreview, setHeadshotPreview] = useState(null);
  const [photoPreviews, setPhotoPreviews] = useState([]);

  // Handle input changes
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    autoSave();
  };

  // Auto-save functionality
  const autoSave = () => {
    setAutoSaveStatus('Saving...');
    setTimeout(() => {
      localStorage.setItem('storySubmissionDraft', JSON.stringify(formData));
      setAutoSaveStatus(`Saved at ${new Date().toLocaleTimeString()}`);
    }, 500);
  };

  // Headshot upload
  const onHeadshotDrop = (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      
      // Validate file
      if (!file.type.startsWith('image/')) {
        setError('Please upload an image file (JPEG or PNG)');
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }

      setFormData(prev => ({ ...prev, headshot: file }));
      setHeadshotPreview(URL.createObjectURL(file));
      setError('');
    }
  };

  const { getRootProps: getHeadshotRootProps, getInputProps: getHeadshotInputProps } = useDropzone({
    onDrop: onHeadshotDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png']
    },
    maxFiles: 1
  });

  // Additional photos upload
  const onPhotosDrop = (acceptedFiles) => {
    const currentCount = formData.additionalPhotos.length;
    
    if (currentCount + acceptedFiles.length > 10) {
      setError('Maximum 10 additional photos allowed');
      return;
    }

    const validFiles = acceptedFiles.filter(file => {
      if (!file.type.startsWith('image/')) {
        setError('Only image files are allowed');
        return false;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError('Each file must be less than 10MB');
        return false;
      }
      return true;
    });

    setFormData(prev => ({
      ...prev,
      additionalPhotos: [...prev.additionalPhotos, ...validFiles]
    }));

    const newPreviews = validFiles.map(file => URL.createObjectURL(file));
    setPhotoPreviews(prev => [...prev, ...newPreviews]);
    setError('');
  };

  const { getRootProps: getPhotosRootProps, getInputProps: getPhotosInputProps } = useDropzone({
    onDrop: onPhotosDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/heic': ['.heic']
    },
    maxFiles: 10
  });

  // Remove photo
  const removePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      additionalPhotos: prev.additionalPhotos.filter((_, i) => i !== index)
    }));
    setPhotoPreviews(prev => prev.filter((_, i) => i !== index));
  };

  // Validation for each step
  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!formData.fullName || !formData.email || !formData.phone) {
          setError('Please fill in all required fields');
          return false;
        }
        if (!/^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/.test(formData.email)) {
          setError('Please enter a valid email address');
          return false;
        }
        break;
      case 3:
        if (!formData.story || formData.story.length < 100) {
          setError('Your story must be at least 100 characters');
          return false;
        }
        if (formData.story.length > 2000) {
          setError('Your story must be less than 2000 characters');
          return false;
        }
        break;
      case 4:
        if (!formData.headshot) {
          setError('Please upload your headshot');
          return false;
        }
        break;
      case 5:
        if (!formData.consent) {
          setError('You must agree to the terms to submit your story');
          return false;
        }
        break;
    }
    setError('');
    return true;
  };

  // Navigate steps
  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Submit form
  const handleSubmit = async () => {
    if (!validateStep(5)) return;

    setLoading(true);
    setError('');

    try {
      // Upload headshot to Firebase Storage
      const headshotRef = ref(storage, `submissions/${user.uid}/${Date.now()}_headshot_${formData.headshot.name}`);
      await uploadBytes(headshotRef, formData.headshot);
      const headshotURL = await getDownloadURL(headshotRef);

      // Upload additional photos
      const photoURLs = await Promise.all(
        formData.additionalPhotos.map(async (photo, index) => {
          const photoRef = ref(storage, `submissions/${user.uid}/${Date.now()}_photo_${index}_${photo.name}`);
          await uploadBytes(photoRef, photo);
          return await getDownloadURL(photoRef);
        })
      );

      // Create submission document
      const submissionData = {
        userId: user.uid,
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        contactMethod: formData.contactMethod,
        socialMedia: {
          instagram: formData.instagram,
          facebook: formData.facebook,
          linkedin: formData.linkedin,
          website: formData.website
        },
        story: formData.story,
        storyTheme: formData.storyTheme,
        headshotURL,
        photoURLs,
        captions: formData.captions,
        status: 'submitted',
        createdAt: new Date().toISOString(),
        timeline: [
          {
            title: 'Story Submitted',
            description: 'Your story has been received and is being reviewed',
            timestamp: new Date().toISOString()
          }
        ]
      };

      // Save to Firestore
      const docRef = await addDoc(collection(db, 'submissions'), submissionData);

      // Clear local storage
      localStorage.removeItem('storySubmissionDraft');

      // TODO: Trigger Cloud Function to:
      // 1. Create Google Drive folder
      // 2. Upload files to Drive
      // 3. Send confirmation email
      // 4. Notify team

      // Navigate to success page
      navigate('/submission-success', { state: { submissionId: docRef.id } });
    } catch (err) {
      console.error('Submission error:', err);
      setError('Failed to submit your story. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Full Name *
              </label>
              <input
                type="text"
                value={formData.fullName}
                onChange={(e) => handleChange('fullName', e.target.value)}
                placeholder="John Doe"
                className="input"
                maxLength={100}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="john@example.com"
                className="input"
                disabled
              />
              <p className="text-xs text-neutral-500 mt-1">This is your login email and cannot be changed</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="+1 (555) 123-4567"
                className="input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Preferred Contact Method *
              </label>
              <div className="flex gap-4">
                {['email', 'whatsapp', 'phone'].map(method => (
                  <label key={method} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="contactMethod"
                      value={method}
                      checked={formData.contactMethod === method}
                      onChange={(e) => handleChange('contactMethod', e.target.value)}
                      className="w-4 h-4 text-primary-600"
                    />
                    <span className="text-sm capitalize">{method}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <p className="text-sm text-neutral-600">
              These will appear on your published page if provided (all optional)
            </p>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                <Instagram className="inline w-4 h-4 mr-1" />
                Instagram Handle
              </label>
              <input
                type="text"
                value={formData.instagram}
                onChange={(e) => handleChange('instagram', e.target.value)}
                placeholder="@yourusername"
                className="input"
                maxLength={30}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                <Facebook className="inline w-4 h-4 mr-1" />
                Facebook Profile URL
              </label>
              <input
                type="url"
                value={formData.facebook}
                onChange={(e) => handleChange('facebook', e.target.value)}
                placeholder="https://facebook.com/yourprofile"
                className="input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                <Linkedin className="inline w-4 h-4 mr-1" />
                LinkedIn Profile URL
              </label>
              <input
                type="url"
                value={formData.linkedin}
                onChange={(e) => handleChange('linkedin', e.target.value)}
                placeholder="https://linkedin.com/in/yourprofile"
                className="input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                <Globe className="inline w-4 h-4 mr-1" />
                Personal Website
              </label>
              <input
                type="url"
                value={formData.website}
                onChange={(e) => handleChange('website', e.target.value)}
                placeholder="https://yourwebsite.com"
                className="input"
              />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Your Story / Journey *
              </label>
              <textarea
                value={formData.story}
                onChange={(e) => handleChange('story', e.target.value)}
                placeholder="Share your story here... Tell us about your journey, experiences, and what makes your story unique."
                className="input min-h-[300px] resize-y"
                maxLength={2000}
              />
              <div className="flex justify-between mt-2">
                <p className="text-xs text-neutral-500">
                  Minimum 100 characters required
                </p>
                <p className={`text-xs ${formData.story.length > 2000 ? 'text-red-600' : 'text-neutral-500'}`}>
                  {formData.story.length} / 2000 characters
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Story Theme
              </label>
              <select
                value={formData.storyTheme}
                onChange={(e) => handleChange('storyTheme', e.target.value)}
                className="input"
              >
                <option value="">Select a theme...</option>
                <option value="family-legacy">Family Legacy</option>
                <option value="career-journey">Career Journey</option>
                <option value="life-challenge">Life Challenge</option>
                <option value="travel-adventure">Travel Adventure</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            {/* Headshot Upload */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Headshot Photo *
              </label>
              <div
                {...getHeadshotRootProps()}
                className="border-2 border-dashed border-neutral-300 rounded-xl p-8 text-center cursor-pointer hover:border-primary-500 hover:bg-primary-50/50 transition-all"
              >
                <input {...getHeadshotInputProps()} />
                {headshotPreview ? (
                  <div className="relative inline-block">
                    <img
                      src={headshotPreview}
                      alt="Headshot preview"
                      className="w-48 h-48 object-cover rounded-xl mx-auto"
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setFormData(prev => ({ ...prev, headshot: null }));
                        setHeadshotPreview(null);
                      }}
                      className="absolute -top-2 -right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
                    <p className="text-neutral-700 font-medium mb-1">
                      Drop your headshot here, or click to browse
                    </p>
                    <p className="text-xs text-neutral-500">
                      JPEG or PNG, min 800x800px, max 5MB
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Additional Photos */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Additional Photos (Optional, max 10)
              </label>
              <div
                {...getPhotosRootProps()}
                className="border-2 border-dashed border-neutral-300 rounded-xl p-6 text-center cursor-pointer hover:border-primary-500 hover:bg-primary-50/50 transition-all"
              >
                <input {...getPhotosInputProps()} />
                <ImageIcon className="w-10 h-10 text-neutral-400 mx-auto mb-2" />
                <p className="text-sm text-neutral-700">
                  Drop photos here or click to browse
                </p>
                <p className="text-xs text-neutral-500 mt-1">
                  {formData.additionalPhotos.length} / 10 photos uploaded
                </p>
              </div>

              {/* Photo Previews */}
              {photoPreviews.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                  {photoPreviews.map((preview, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={preview}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg"
                      />
                      <button
                        onClick={() => removePhoto(index)}
                        className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <input
                        type="text"
                        placeholder="Caption (optional)"
                        value={formData.captions[index] || ''}
                        onChange={(e) => handleChange('captions', { ...formData.captions, [index]: e.target.value })}
                        className="w-full mt-2 px-2 py-1 text-xs border border-neutral-300 rounded"
                        maxLength={100}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="bg-neutral-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-neutral-900 mb-4">
                Terms & Consent
              </h3>
              <div className="space-y-4 text-sm text-neutral-700">
                <p>
                  By submitting your story to Stories for Memories, you confirm and agree to the following:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-2">
                  <li>You own the rights to all submitted content (text and images)</li>
                  <li>You consent to publication in Stories for Memories magazine</li>
                  <li>Your story may be shared on our website and social media platforms</li>
                  <li>We may edit your submission for clarity, length, and grammar</li>
                  <li>You release Stories for Memories from any liability related to publication</li>
                </ul>
              </div>
            </div>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.consent}
                onChange={(e) => handleChange('consent', e.target.checked)}
                className="mt-1 w-5 h-5 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
              />
              <span className="text-sm text-neutral-700">
                I confirm that I own the rights to all submitted content and consent to publication in 
                Stories for Memories magazine. I have read and agree to the{' '}
                <a href="/terms" target="_blank" className="text-primary-600 hover:underline">
                  Terms of Use
                </a>{' '}
                and{' '}
                <a href="/privacy" target="_blank" className="text-primary-600 hover:underline">
                  Privacy Policy
                </a>.
              </span>
            </label>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-neutral-600 hover:text-neutral-900 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>
          <h1 className="text-3xl font-display font-semibold text-neutral-900 mb-2">
            Share Your Story
          </h1>
          <p className="text-neutral-600">
            Estimated time: 10 minutes · {autoSaveStatus && (
              <span className="text-sm text-green-600">{autoSaveStatus}</span>
            )}
          </p>
        </div>

        {/* Progress Steps */}
        <div className="card p-6 mb-6">
          <div className="flex items-center justify-between mb-2">
            {STEPS.map((step, index) => {
              const StepIcon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;

              return (
                <React.Fragment key={step.id}>
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 transition-all ${
                        isActive
                          ? 'bg-primary-600 text-white scale-110'
                          : isCompleted
                          ? 'bg-green-500 text-white'
                          : 'bg-neutral-200 text-neutral-500'
                      }`}
                    >
                      {isCompleted ? (
                        <Check className="w-6 h-6" />
                      ) : (
                        <StepIcon className="w-6 h-6" />
                      )}
                    </div>
                    <span
                      className={`text-xs font-medium text-center hidden md:block ${
                        isActive || isCompleted ? 'text-neutral-900' : 'text-neutral-500'
                      }`}
                    >
                      {step.title}
                    </span>
                  </div>
                  {index < STEPS.length - 1 && (
                    <div
                      className={`flex-1 h-0.5 mx-2 ${
                        isCompleted ? 'bg-green-500' : 'bg-neutral-200'
                      }`}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Form Content */}
        <div className="card p-8 mb-6">
          {/* Error Message */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl flex items-start gap-2">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Step Content */}
          <div className="animate-fade-in">
            <h2 className="text-2xl font-semibold text-neutral-900 mb-6">
              {STEPS[currentStep - 1].title}
            </h2>
            {renderStepContent()}
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center">
          <button
            onClick={prevStep}
            disabled={currentStep === 1}
            className="btn btn-outline disabled:opacity-50"
          >
            <ArrowLeft className="w-5 h-5" />
            Previous
          </button>

          {currentStep < 5 ? (
            <button onClick={nextStep} className="btn btn-primary">
              Next
              <ArrowRight className="w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="btn btn-primary"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 spinner" />
                  Submitting...
                </>
              ) : (
                <>
                  <Check className="w-5 h-5" />
                  Submit Story
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
